import {
  Children,
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { TabList } from './TabList';
import { TabPanel } from './TabPanel';

/**
 * Tabs Component
 *
 * A flexible tabs component that manages tab navigation with support for
 * multiple visual variants (border or background style). The component
 * orchestrates TabList and TabPanel children to create a complete tabbed interface.
 *
 * Features:
 * - Controlled and uncontrolled tab selection
 * - Custom styling for different parts (root, tab list, tab panel)
 * - Two visual variants: border-based and background-based highlighting
 * - Automatic active state management
 * - Callback support for tab changes
 *
 * @example
 * ```tsx
 * <Tabs initialTab={0} onChangeTab={(index) => console.log(index)}>
 *   <TabList>
 *     <Tab title="Tab 1" />
 *     <Tab title="Tab 2" />
 *   </TabList>
 *   <TabPanel>
 *     <div>Content for Tab 1</div>
 *     <div>Content for Tab 2</div>
 *   </TabPanel>
 * </Tabs>
 * ```
 */

interface ITabsProps {
  /** The tab panels or tab items to render */
  children: ReactNode;
  /** The index of the tab that should be active by default */
  initialTab?: number;
  /** Callback fired when the active tab changes */
  onChangeTab?: (index: number) => void;
  /** Custom styles applied to the root container of the Tabs component */
  style?: CSSProperties;
  /** Custom styles applied to the tab panel container */
  tabPanelStyle?: CSSProperties;
  /** Custom styles applied to the tab list container */
  tabListStyle?: CSSProperties;
  /** The visual style used to highlight the active tab */
  variant?: 'border' | 'background';
}

export const Tabs = ({
  children,
  initialTab = 0,
  onChangeTab = () => {},
  style = {},
  tabPanelStyle = {},
  tabListStyle = {},
  variant = 'border',
}: ITabsProps) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    onChangeTab(activeTab);
  }, [activeTab, onChangeTab]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...style,
      }}
    >
      {Children.map(children, (child) => {
        const element = child as ReactElement<{ children: ReactNode }>;
        if (
          element?.type &&
          typeof element.type === 'function' &&
          (element.type as any).displayName === 'TabList'
        ) {
          return (
            <TabList
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              style={tabListStyle}
              variant={variant}
            >
              {element.props.children}
            </TabList>
          );
        }
        return null;
      })}
      {Children.map(children, (child) => {
        const element = child as ReactElement<{ children: ReactNode[] | ReactNode }>;
        if (
          element?.type &&
          typeof element.type === 'function' &&
          (element.type as any).displayName === 'TabPanel'
        ) {
          return (
            <TabPanel key={activeTab} style={tabPanelStyle}>
              {Array.isArray(element.props.children)
                ? element.props.children[activeTab]
                : element.props.children}
            </TabPanel>
          );
        }
        return null;
      })}
    </div>
  );
};
